import {
  lazy,
  Suspense,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  ArrowDown,
  ArrowUp,
  ArrowLeft,
  Database,
  Download,
  FilePlus2,
  LayoutDashboard,
  Pencil,
  Plus,
  RefreshCw,
  Settings2,
  Trash2,
  Printer,
} from "lucide-react";
import { AppShell } from "./app-shell";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  BindingSettings,
  Field,
  ParameterFields,
  PlacementEditor,
  TemplateEditor,
  TextArea,
  WidgetEditor,
  editorPanel,
} from "./reporting-editors";
import {
  ReportDocument,
  ReportingWidget,
  downloadResult,
} from "./reporting-renderer";
import { QueryProvider, useEngine, useQuery } from "@/lib/query-context";
import {
  bindingEditor,
  bindingText,
  bindingUsages,
  defaultsFor,
  deleteDefinition,
  generateReport,
  mapWidgetInputs,
  widgetUsages,
  type DataBindingDefinition,
  type GeneratedReport,
  type Parameters,
  type ReportTemplateDefinition,
  type ReportingDashboard,
  type ReportingState,
  type WidgetDefinition,
  type WidgetPlacement,
} from "@/lib/reporting";
import {
  readReportingState,
  reportRepository,
  saveReportingState,
} from "@/lib/reporting-storage";
import { defaultReportingState } from "@/lib/nclean-catalog";
import { NCLEAN_AS_OF } from "@/lib/nclean-data";
import { getLocale, tx } from "@/lib/i18n";
import { useLocale } from "@/lib/i18n-react";
import { visualizationError } from "@/lib/presentation";
import { simpleQuery } from "@/lib/guided-query";
import type {
  PageId,
  QueryContext,
  QueryDefinition,
  QueryResult,
} from "@/lib/types";

const ReportBuilder = lazy(() =>
  import("./report-builder").then((m) => ({ default: m.ReportBuilder })),
);
const DataModel = lazy(() =>
  import("./data-model").then((m) => ({ default: m.DataModel })),
);
const CONTEXT: QueryContext = {
  operatingUnitId: "north-coast",
  propertyId: "all",
  from: "2026-05-01",
  to: "2026-11-15",
};
const nameOf = (item: { name: string; builtin?: boolean }) =>
  item.builtin ? tx(item.name) : item.name;
const descriptionOf = (item: { description: string; builtin?: boolean }) =>
  item.builtin ? tx(item.description) : item.description;
function readRoute(): { page: PageId; id?: string } {
  const [raw, id] = location.hash.replace(/^#\/?/, "").split("/");
  return {
    page: [
      "dashboards",
      "widgets",
      "bindings",
      "templates",
      "reports",
      "builder",
      "data-model",
    ].includes(raw)
      ? (raw as PageId)
      : "dashboards",
    id,
  };
}
export default function ReportingWorkspace() {
  const [refresh, setRefresh] = useState(0),
    [context, setContext] = useState(CONTEXT);
  return (
    <QueryProvider context={context} refreshVersion={refresh}>
      <Workspace
        context={context}
        onContextChange={setContext}
        onRefresh={() => setRefresh((v) => v + 1)}
      />
    </QueryProvider>
  );
}
function Workspace({
  context,
  onContextChange,
  onRefresh,
}: {
  context: QueryContext;
  onContextChange: (c: QueryContext) => void;
  onRefresh: () => void;
}) {
  useLocale();
  const [initial] = useState(() => {
    try {
      return { state: readReportingState(), error: "" };
    } catch (e) {
      return { state: defaultReportingState(), error: (e as Error).message };
    }
  });
  const [state, setState] = useState(initial.state),
    [error, setError] = useState(initial.error),
    [toast, setToast] = useState("");
  const [route, setRoute] = useState(readRoute),
    [reports, setReports] = useState<GeneratedReport[]>([]),
    [reportsReady, setReportsReady] = useState(false);
  const [search, setSearch] = useState(""),
    [generationTemplate, setGenerationTemplate] =
      useState<ReportTemplateDefinition>();
  const [generationValues, setGenerationValues] = useState<Parameters>({}),
    [recovered, setRecovered] = useState(false);
  const [confirmation, setConfirmation] = useState<{
    kind: "widgets" | "templates" | "dashboards" | "reset";
    id: string;
    name: string;
  }>();
  const [bindingDraft, setBindingDraft] = useState<DataBindingDefinition>();
  const engine = useEngine();
  useEffect(() => {
    const listener = () => {
      setRoute(readRoute());
      setSearch("");
      setBindingDraft(undefined);
    };
    window.addEventListener("hashchange", listener);
    return () => window.removeEventListener("hashchange", listener);
  }, []);
  useEffect(() => {
    if (!initial.error) {
      try {
        saveReportingState(initial.state);
      } catch (e) {
        setError((e as Error).message);
      }
    }
  }, [initial]);
  useEffect(() => {
    let active = true;
    void reportRepository.list().then(
      (list) => {
        if (active) {
          setReports(list);
          setReportsReady(true);
        }
      },
      (e) => {
        if (active) {
          setError(e.message);
          setReportsReady(true);
        }
      },
    );
    return () => {
      active = false;
    };
  }, []);
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(""), 5000);
    return () => clearTimeout(timer);
  }, [toast]);
  useEffect(() => {
    if (route.page !== "dashboards" || !route.id) return;
    const timer = setInterval(() => {
      if (document.visibilityState === "visible") onRefresh();
    }, 60000);
    return () => clearInterval(timer);
  }, [route.page, route.id, onRefresh]);
  const navigate = (page: PageId, id?: string) => {
    location.hash = `#/${page}${id ? `/${id}` : ""}`;
    setRoute({ page, id });
    setSearch("");
    setBindingDraft(undefined);
    window.scrollTo({ top: 0, behavior: "instant" });
  };
  const commit = (next: ReportingState, force = false) => {
    if (initial.error && !recovered && !force) {
      setError(initial.error);
      return false;
    }
    try {
      if (force) setRecovered(true);
      saveReportingState(next);
      setState(next);
      setError("");
      return true;
    } catch (e) {
      setError((e as Error).message);
      return false;
    }
  };
  const updateDashboard = (dashboard: ReportingDashboard) =>
    commit({
      ...state,
      dashboards: state.dashboards.map((d) =>
        d.id === dashboard.id ? dashboard : d,
      ),
    });
  const confirmDelete = (
    kind: "widgets" | "templates" | "dashboards",
    item: { id: string; name: string },
  ) => setConfirmation({ kind, id: item.id, name: item.name });
  const matches = (item: { name: string; description: string }) =>
    `${nameOf(item)} ${descriptionOf(item)}`
      .toLowerCase()
      .includes(search.toLowerCase());
  const currentDashboard = state.dashboards.find((d) => d.id === route.id),
    currentWidget = state.widgets.find((w) => w.id === route.id),
    currentTemplate = state.templates.find((t) => t.id === route.id),
    currentReport = reports.find((r) => r.id === route.id);
  const storedBinding = state.bindings.find((b) => b.id === route.id),
    binding = bindingDraft?.id === route.id ? bindingDraft : storedBinding;
  const pageLabel =
    route.page === "builder"
      ? "Data bindings"
      : route.page === "data-model"
        ? "Data model"
        : route.page === "bindings"
          ? "Data bindings"
          : route.page === "widgets"
            ? "Widgets"
            : route.page === "templates"
              ? "Report Templates"
              : route.page === "reports"
                ? "Reports"
                : "Dashboards";
  const breadcrumbs = [
    { label: tx("Analytics"), page: "dashboards" as const },
    {
      label: tx(pageLabel),
      ...(route.page === "builder" ? { page: "bindings" as const } : {}),
    },
    ...(route.id
      ? [
          {
            label:
              (route.page === "dashboards"
                ? currentDashboard
                : route.page === "widgets"
                  ? currentWidget
                  : route.page === "templates"
                    ? currentTemplate
                    : route.page === "reports"
                      ? currentReport
                      : binding
              )?.name ?? tx("Not found"),
          },
        ]
      : []),
  ];
  const createBinding = () => {
    const b: DataBindingDefinition = {
      id: crypto.randomUUID(),
      name: tx("Untitled data binding"),
      revision: 1,
      parameters: [],
      inputMapping: { rows: "$rows" },
      query: {
        mode: "guided",
        query: simpleQuery("nclean_billing", ["recorded_quantity"]),
      },
    };
    if (commit({ ...state, bindings: [...state.bindings, b] }))
      navigate("builder", b.id);
  };
  const saveBinding = (report: QueryDefinition, data?: QueryResult) => {
    try {
      if (!binding || !data)
        throw new Error("Preview the data binding before saving.");
      const next: DataBindingDefinition = {
        ...binding,
        name: report.name,
        revision: binding.revision + 1,
        query:
          report.mode === "guided"
            ? { mode: "guided", query: report.query }
            : {
                mode: "malloy",
                malloy: report.malloy,
                guidedDraft: report.guidedDraft,
              },
      };
      const referenced = new Set([
        ...state.dashboards.flatMap((d) =>
          d.widgets
            .filter((p) => p.bindingId === next.id)
            .map((p) => p.widgetId),
        ),
        ...state.templates.flatMap((t) =>
          t.sections.flatMap((s) =>
            s.kind === "widget" && s.bindingId === next.id ? [s.widgetId] : [],
          ),
        ),
      ]);
      for (const id of referenced) {
        const widget = state.widgets.find((w) => w.id === id)!;
        mapWidgetInputs(widget, data, next.inputMapping);
      }
      if (
        commit({
          ...state,
          bindings: state.bindings.map((b) => (b.id === next.id ? next : b)),
        })
      ) {
        setBindingDraft(next);
        setToast("Data binding saved.");
      }
    } catch (e) {
      setError((e as Error).message);
    }
  };
  const saveWidget = async (widget: WidgetDefinition) => {
    const uses = [
      ...state.dashboards.flatMap((d) =>
        d.widgets
          .filter((p) => p.widgetId === widget.id)
          .map((p) => ({
            bindingId: p.bindingId,
            parameters: { ...defaultsFor(d.parameters), ...p.parameters },
            inputMapping: p.inputMapping,
          })),
      ),
      ...state.templates.flatMap((t) =>
        t.sections.flatMap((s) =>
          s.kind === "widget" && s.widgetId === widget.id
            ? [
                {
                  bindingId: s.bindingId,
                  parameters: defaultsFor(t.parameters),
                  inputMapping: undefined,
                },
              ]
            : [],
        ),
      ),
    ];
    for (const use of uses) {
      const b = state.bindings.find((b) => b.id === use.bindingId);
      if (!b) throw new Error("A referenced data binding is missing.");
      const data = mapWidgetInputs(
        widget,
        await engine.query(bindingText(b, use.parameters)),
        { ...b.inputMapping, ...use.inputMapping },
      );
      const invalid = widget.renderer
        ? undefined
        : visualizationError(data, widget.visualization);
      if (invalid) throw new Error(invalid);
    }
    if (
      commit({
        ...state,
        widgets: state.widgets.map((w) => (w.id === widget.id ? widget : w)),
      })
    ) {
      setToast("Widget saved.");
      navigate("widgets");
    }
  };
  const blocked =
    confirmation?.kind === "widgets"
      ? widgetUsages(state, confirmation.id)
      : [];
  return (
    <AppShell
      page={route.page}
      breadcrumbs={breadcrumbs}
      onNavigate={(page) => navigate(page)}
      onReset={() =>
        setConfirmation({
          kind: "reset",
          id: "reset",
          name: tx("Restore examples"),
        })
      }
    >
      {error && (
        <div
          role="alert"
          className="no-print mb-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800"
        >
          {error}
          <Button variant="ghost" className="ms-3" onClick={() => setError("")}>
            {tx("Dismiss")}
          </Button>
        </div>
      )}
      <Suspense fallback={<p role="status">{tx("Loading…")}</p>}>
        {route.page === "dashboards" && !route.id && (
          <>
            <LibraryHeader
              title="Dashboards"
              description="Widgets connected to current data. Source and refresh times are shown separately."
              search={search}
              onSearch={setSearch}
              action={
                <Button
                  onClick={() => {
                    const d: ReportingDashboard = {
                      id: crypto.randomUUID(),
                      name: tx("Untitled dashboard"),
                      description: "",
                      widgets: [],
                      parameters: [],
                      sourceAt: NCLEAN_AS_OF,
                    };
                    if (
                      commit({ ...state, dashboards: [...state.dashboards, d] })
                    )
                      navigate("dashboards", d.id);
                  }}
                >
                  <Plus />
                  {tx("New dashboard")}
                </Button>
              }
            />
            <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
              {state.dashboards.filter(matches).map((d) => (
                <div className={editorPanel} key={d.id}>
                  <div className="flex items-start justify-between">
                    <span className="rounded-lg bg-teal-50 p-3 text-teal-700">
                      <LayoutDashboard />
                    </span>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`${tx("Delete dashboard")} ${nameOf(d)}`}
                      onClick={() => confirmDelete("dashboards", d)}
                    >
                      <Trash2 />
                    </Button>
                  </div>
                  <h2 className="text-lg font-semibold">{nameOf(d)}</h2>
                  <p className="min-h-10 text-sm text-muted-foreground">
                    {descriptionOf(d)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {d.widgets.length} {tx("Widgets")} · {tx("Source as of")}{" "}
                    {d.sourceAt.slice(0, 10)}
                  </p>
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => navigate("dashboards", d.id)}
                  >
                    {tx("Open dashboard")}
                  </Button>
                </div>
              ))}
            </div>
            {!state.dashboards.filter(matches).length && <EmptyLibrary />}
          </>
        )}
        {route.page === "dashboards" &&
          route.id &&
          (currentDashboard ? (
            <DashboardView
              key={currentDashboard.id}
              dashboard={currentDashboard}
              state={state}
              onUpdate={updateDashboard}
              onBack={() => navigate("dashboards")}
              onRefresh={onRefresh}
              onEditBinding={(id) => navigate("builder", id)}
              onDelete={() => confirmDelete("dashboards", currentDashboard)}
              onGenerate={(values) => {
                setGenerationValues(values);
                const id =
                  currentDashboard.id === "daily-operations"
                    ? "daily-work-log"
                    : currentDashboard.id === "lost-property"
                      ? "register-snapshot"
                      : currentDashboard.id;
                const template = state.templates.find((t) => t.id === id);
                if (template) setGenerationTemplate(template);
                else navigate("templates");
              }}
            />
          ) : (
            <EmptyLibrary />
          ))}
        {route.page === "widgets" && !route.id && (
          <>
            <LibraryHeader
              title="Widgets"
              description="Reusable typed inputs and visualizations. Data queries are configured separately."
              search={search}
              onSearch={setSearch}
              action={
                <div className="flex gap-2">
                  <Button
                    onClick={() => {
                      const w: WidgetDefinition = {
                        id: crypto.randomUUID(),
                        name: tx("Untitled Widget"),
                        description: "",
                        revision: 1,
                        inputs: [
                          { name: "rows", type: "array", nullable: false },
                        ],
                        visualization: {
                          type: "table",
                          x: "",
                          y: [],
                          format: "number",
                        },
                      };
                      if (commit({ ...state, widgets: [...state.widgets, w] }))
                        navigate("widgets", w.id);
                    }}
                  >
                    <Plus />
                    {tx("New Widget")}
                  </Button>
                </div>
              }
            />
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {state.widgets.filter(matches).map((w) => (
                <section
                  className="flex h-80 min-w-0 flex-col gap-3 rounded-xl border border-border bg-card p-4"
                  key={w.id}
                >
                  <div className="flex min-w-0 items-center justify-between gap-2">
                    <h2
                      className="truncate text-sm font-semibold"
                      title={nameOf(w)}
                    >
                      {nameOf(w)}
                    </h2>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 shrink-0"
                      aria-label={`${tx("Delete Widget")} ${nameOf(w)}`}
                      onClick={() => confirmDelete("widgets", w)}
                    >
                      <Trash2 />
                    </Button>
                  </div>
                  <p
                    className="line-clamp-2 h-8 shrink-0 text-xs leading-4 text-muted-foreground"
                    title={descriptionOf(w)}
                  >
                    {descriptionOf(w)}
                  </p>
                  <WidgetConfiguration
                    widget={w}
                    state={state}
                    onOpenBinding={(id) => navigate("builder", id)}
                  />
                  <p className="mt-auto text-xs text-muted-foreground">
                    {widgetUsages(state, w.id).length} {tx("Uses")} ·{" "}
                    {tx("Revision")} {w.revision}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate("widgets", w.id)}
                    >
                      <Pencil />
                      {tx("Edit Widget")}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const copy = {
                          ...structuredClone(w),
                          id: crypto.randomUUID(),
                          name: `${nameOf(w)} — ${tx("copy")}`,
                          builtin: false,
                          revision: 1,
                        };
                        if (
                          commit({
                            ...state,
                            widgets: [...state.widgets, copy],
                          })
                        )
                          navigate("widgets", copy.id);
                      }}
                    >
                      {tx("Duplicate")}
                    </Button>
                  </div>
                </section>
              ))}
            </div>
          </>
        )}
        {route.page === "bindings" && (
          <>
            <LibraryHeader
              title="Data bindings"
              description="Reusable data for your widgets, dashboards and report templates."
              search={search}
              onSearch={setSearch}
              action={
                <Button onClick={createBinding}>
                  <Plus />
                  {tx("New data binding")}
                </Button>
              }
            />
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {state.bindings
                .filter((b) =>
                  nameOf(b).toLowerCase().includes(search.toLowerCase()),
                )
                .map((b) => (
                  <section
                    className={`${editorPanel} flex min-w-0 flex-col`}
                    key={b.id}
                  >
                    <h2 className="font-semibold">{nameOf(b)}</h2>
                    <BindingSummary
                      binding={b}
                      state={state}
                      onOpen={navigate}
                    />
                    <Button
                      variant="outline"
                      onClick={() => navigate("builder", b.id)}
                    >
                      <Pencil />
                      {tx("Edit data binding")}
                    </Button>
                  </section>
                ))}
            </div>
            {!state.bindings.some((b) =>
              nameOf(b).toLowerCase().includes(search.toLowerCase()),
            ) && <EmptyLibrary />}
          </>
        )}
        {route.page === "widgets" &&
          route.id &&
          (currentWidget ? (
            <WidgetEditor
              key={currentWidget.id}
              seed={currentWidget}
              bindings={state.bindings}
              onSave={saveWidget}
              onBack={() => navigate("widgets")}
            />
          ) : (
            <EmptyLibrary />
          ))}
        {route.page === "templates" && !route.id && (
          <>
            <LibraryHeader
              title="Report Templates"
              description="Compose text, conditional sections, Widgets and data queries into repeatable documents."
              search={search}
              onSearch={setSearch}
              action={
                <Button
                  onClick={() => {
                    const t: ReportTemplateDefinition = {
                      id: crypto.randomUUID(),
                      name: tx("Untitled Report Template"),
                      description: "",
                      revision: 1,
                      parameters: [],
                      bindingIds: [],
                      sections: [
                        {
                          id: crypto.randomUUID(),
                          kind: "text",
                          title: tx("Text section"),
                          text: "",
                        },
                      ],
                      sourceAt: NCLEAN_AS_OF,
                    };
                    if (
                      commit({ ...state, templates: [...state.templates, t] })
                    )
                      navigate("templates", t.id);
                  }}
                >
                  <Plus />
                  {tx("New Report Template")}
                </Button>
              }
            />
            <div className="grid gap-5 md:grid-cols-2">
              {state.templates.filter(matches).map((t) => (
                <section className={editorPanel} key={t.id}>
                  <div className="flex justify-between gap-3">
                    <h2 className="text-lg font-semibold">{nameOf(t)}</h2>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`${tx("Delete Report Template")} ${nameOf(t)}`}
                      onClick={() => confirmDelete("templates", t)}
                    >
                      <Trash2 />
                    </Button>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {descriptionOf(t)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {t.sections.length} {tx("Sections")} · {tx("Revision")}{" "}
                    {t.revision}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      onClick={() => {
                        setGenerationValues({});
                        setGenerationTemplate(t);
                      }}
                    >
                      <FilePlus2 />
                      {tx("Generate Report")}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => navigate("templates", t.id)}
                    >
                      <Pencil />
                      {tx("Edit template")}
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => {
                        const copy = {
                          ...structuredClone(t),
                          id: crypto.randomUUID(),
                          name: `${nameOf(t)} — ${tx("copy")}`,
                          builtin: false,
                          revision: 1,
                        };
                        if (
                          commit({
                            ...state,
                            templates: [...state.templates, copy],
                          })
                        )
                          navigate("templates", copy.id);
                      }}
                    >
                      {tx("Duplicate")}
                    </Button>
                  </div>
                </section>
              ))}
            </div>
            {!state.templates.filter(matches).length && <EmptyLibrary />}
          </>
        )}
        {route.page === "templates" &&
          route.id &&
          (currentTemplate ? (
            <TemplateEditor
              key={currentTemplate.id}
              seed={currentTemplate}
              state={state}
              onBack={() => navigate("templates")}
              onSave={(t) => {
                if (
                  commit({
                    ...state,
                    templates: state.templates.map((item) =>
                      item.id === t.id ? t : item,
                    ),
                  })
                ) {
                  setToast("Report Template saved.");
                  navigate("templates");
                }
              }}
            />
          ) : (
            <EmptyLibrary />
          ))}
        {route.page === "reports" && !route.id && (
          <>
            <LibraryHeader
              title="Reports"
              description="Saved document snapshots. Changes to templates, Widgets or source data do not change these Reports."
              search={search}
              onSearch={setSearch}
              action={
                <Button onClick={() => navigate("templates")}>
                  <FilePlus2 />
                  {tx("Generate from template")}
                </Button>
              }
            />
            {!reportsReady && <p role="status">{tx("Loading…")}</p>}
            <div className="grid gap-4 md:grid-cols-2">
              {reports
                .filter((r) =>
                  r.name.toLowerCase().includes(search.toLowerCase()),
                )
                .map((r) => (
                  <section className={editorPanel} key={r.id}>
                    <h2 className="font-semibold">{r.name}</h2>
                    <p className="text-sm text-muted-foreground">
                      {Object.entries(r.parameters)
                        .map(([k, v]) => `${k}: ${v}`)
                        .join(" · ")}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {tx("Generated")}{" "}
                      {new Date(r.generatedAt).toLocaleString()} ·{" "}
                      {r.locale.toUpperCase()}
                    </p>
                    <Button
                      variant="outline"
                      onClick={() => navigate("reports", r.id)}
                    >
                      {tx("Open Report")}
                    </Button>
                  </section>
                ))}
            </div>
            {reportsReady && !reports.length && (
              <div className={`${editorPanel} text-center`}>
                <h2 className="font-semibold">
                  {tx("No generated Reports yet")}
                </h2>
                <p className="text-sm text-muted-foreground">
                  {tx(
                    "Choose a Report Template and generate its first document snapshot.",
                  )}
                </p>
                <Button onClick={() => navigate("templates")}>
                  {tx("View Report Templates")}
                </Button>
              </div>
            )}
          </>
        )}
        {route.page === "reports" &&
          route.id &&
          (currentReport ? (
            <>
              <div className="no-print mb-5 flex justify-between gap-2">
                <Button variant="outline" onClick={() => navigate("reports")}>
                  <ArrowLeft />
                  {tx("Reports")}
                </Button>
                <Button onClick={() => window.print()}>
                  <Printer />
                  {tx("Print / Save PDF")}
                </Button>
              </div>
              <ReportDocument report={currentReport} />
            </>
          ) : reportsReady ? (
            <EmptyLibrary />
          ) : (
            <p>{tx("Loading…")}</p>
          ))}
        {route.page === "builder" && binding && (
          <div className="flex h-full min-h-0 flex-col gap-3">
            <div className="flex shrink-0 justify-between gap-2">
              <Button variant="outline" onClick={() => navigate("bindings")}>
                <ArrowLeft />
                {tx("Data bindings")}
              </Button>
              <Button variant="outline" onClick={() => navigate("data-model")}>
                <Database />
                {tx("Data model")}
              </Button>
            </div>
            <div className="min-h-[650px] flex-1">
              <ReportBuilder
                key={binding.id}
                seed={bindingEditor(storedBinding ?? binding)}
                context={context}
                onContextChange={onContextChange}
                onRefresh={onRefresh}
                onSave={saveBinding}
                onOpenDataModel={() => navigate("data-model")}
                notify={setToast}
                bindingMode
                bindingSettings={
                  <BindingSettings
                    binding={binding}
                    onChange={setBindingDraft}
                    usage={bindingUsages(state, binding.id)}
                  />
                }
                previewTransform={(text) => {
                  try {
                    return bindingText({
                      ...binding,
                      query: { mode: "malloy", malloy: text },
                    });
                  } catch {
                    return text;
                  }
                }}
              />
            </div>
          </div>
        )}
        {route.page === "data-model" && (
          <DataModel
            onBack={() => navigate("bindings")}
            onRefresh={onRefresh}
          />
        )}
      </Suspense>
      <Dialog
        open={!!confirmation}
        onOpenChange={(open) => {
          if (!open) setConfirmation(undefined);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {tx(
                blocked.length
                  ? "Widget is in use"
                  : confirmation?.kind === "reset"
                    ? "Restore examples"
                    : "Delete definition",
              )}
            </DialogTitle>
            <DialogDescription>
              {blocked.length
                ? tx(
                    "Remove or replace this Widget in every listed definition before deleting it.",
                  )
                : confirmation?.kind === "reset"
                  ? tx(
                      "Restore the NClean definitions. Generated Reports and the migration backup are retained.",
                    )
                  : tx(
                      "This removes the definition. Generated Reports remain unchanged.",
                    )}
            </DialogDescription>
          </DialogHeader>
          <p className="font-medium">{confirmation?.name}</p>
          {blocked.map((use) => (
            <Button
              key={`${use.kind}-${use.id}`}
              variant="outline"
              onClick={() => {
                setConfirmation(undefined);
                navigate(use.kind, use.id);
              }}
            >
              {use.name}
            </Button>
          ))}
          <div className="mt-3 flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setConfirmation(undefined)}
            >
              {tx("Cancel")}
            </Button>
            {!blocked.length && (
              <Button
                variant="destructive"
                onClick={() => {
                  if (!confirmation) return;
                  const next =
                    confirmation.kind === "reset"
                      ? defaultReportingState()
                      : deleteDefinition(
                          state,
                          confirmation.kind,
                          confirmation.id,
                        );
                  if (commit(next, confirmation.kind === "reset")) {
                    const page =
                      confirmation.kind === "reset"
                        ? "dashboards"
                        : confirmation.kind;
                    setConfirmation(undefined);
                    navigate(page);
                    setToast("Changes saved.");
                  }
                }}
              >
                {tx(
                  confirmation?.kind === "reset"
                    ? "Restore examples"
                    : "Delete",
                )}
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
      {generationTemplate && (
        <GenerationDialog
          key={generationTemplate.id}
          template={generationTemplate}
          state={state}
          initialValues={generationValues}
          onClose={() => setGenerationTemplate(undefined)}
          onSaved={(report) => {
            setReports((previous) => [report, ...previous]);
            setGenerationTemplate(undefined);
            navigate("reports", report.id);
            setToast("Report generated and saved.");
          }}
        />
      )}
      {toast && (
        <div
          role="status"
          className="no-print fixed bottom-5 end-5 z-[100] rounded-xl border border-border bg-white px-5 py-3 text-sm shadow-xl"
        >
          {tx(toast)}
        </div>
      )}
    </AppShell>
  );
}
function LibraryHeader({
  title,
  description,
  search,
  onSearch,
  action,
}: {
  title: string;
  description: string;
  search: string;
  onSearch: (s: string) => void;
  action: ReactNode;
}) {
  return (
    <header className="mb-6 space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{tx(title)}</h1>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
            {tx(description)}
          </p>
        </div>
        {action}
      </div>
      <Input
        aria-label={tx("Search library")}
        placeholder={tx("Search library")}
        className="max-w-sm"
        value={search}
        onChange={(e) => onSearch(e.target.value)}
      />
    </header>
  );
}
function EmptyLibrary() {
  return (
    <div
      className={`${editorPanel} py-12 text-center text-sm text-muted-foreground`}
    >
      {tx("No matching definitions")}
    </div>
  );
}

function PlacementView({
  placement,
  dashboard,
  state,
  editing,
  onConfigure,
  onEditBinding,
  onRemove,
  onMove,
  onResolved,
}: {
  placement: WidgetPlacement;
  dashboard: ReportingDashboard;
  state: ReportingState;
  editing: boolean;
  onConfigure: () => void;
  onEditBinding: () => void;
  onRemove: () => void;
  onMove: (direction: number) => void;
  onResolved: () => void;
}) {
  const widget = state.widgets.find((w) => w.id === placement.widgetId),
    binding = state.bindings.find((b) => b.id === placement.bindingId);
  const parameters = {
    ...defaultsFor(dashboard.parameters),
    ...placement.parameters,
  };
  let text: string | undefined,
    failure = "";
  try {
    if (binding) text = bindingText(binding, parameters);
  } catch (e) {
    failure = (e as Error).message;
  }
  const result = useQuery(text);
  useEffect(() => {
    if (result.data) onResolved();
  }, [result.data]);
  let data: QueryResult | undefined;
  try {
    if (widget && binding && result.data)
      data = mapWidgetInputs(widget, result.data, {
        ...binding.inputMapping,
        ...placement.inputMapping,
      });
  } catch (e) {
    failure = (e as Error).message;
  }
  const effective = widget
    ? {
        ...widget,
        visualization: { ...widget.visualization, ...placement.visualization },
      }
    : undefined;
  return (
    <section
      className={`${editorPanel} min-w-0 ${placement.width === "full" ? "col-span-12" : placement.width === "half" ? "col-span-12 xl:col-span-6" : placement.width === "third" ? "col-span-12 md:col-span-6 xl:col-span-4" : "col-span-12 md:col-span-6 xl:col-span-3"}`}
    >
      {effective?.visualization.showHeader !== false && (
        <h2 className="text-sm font-semibold">
          {placement.title
            ? tx(placement.title)
            : widget
              ? nameOf(widget)
              : tx("Missing Widget")}
        </h2>
      )}
      {effective?.visualization.showDescription !== false &&
        (placement.description || widget?.description) && (
          <p className="text-xs leading-5 text-muted-foreground">
            {placement.description ?? (widget ? descriptionOf(widget) : "")}
          </p>
        )}
      {editing && (
        <div className="flex flex-wrap gap-1">
          <Button variant="outline" size="sm" onClick={onConfigure}>
            <Settings2 />
            {tx("Configure placement")}
          </Button>
          <Button variant="ghost" size="sm" onClick={onEditBinding}>
            {tx("Edit data binding")}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            aria-label={tx("Move up")}
            onClick={() => onMove(-1)}
          >
            <ArrowUp />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            aria-label={tx("Move down")}
            onClick={() => onMove(1)}
          >
            <ArrowDown />
          </Button>
          <Button
            variant="destructive-ghost"
            size="icon"
            aria-label={tx("Remove placement")}
            onClick={onRemove}
          >
            <Trash2 />
          </Button>
        </div>
      )}
      {(failure || result.error || !widget || !binding) && (
        <p role="alert" className="text-sm text-red-700">
          {failure ||
            result.error ||
            tx("Select an existing Widget and data binding.")}
        </p>
      )}
      {result.loading && (
        <p
          role="status"
          className="py-8 text-center text-sm text-muted-foreground"
        >
          {tx("Loading…")}
        </p>
      )}
      {data && effective && (
        <ReportingWidget
          widget={effective}
          data={data}
          parameters={parameters}
        />
      )}
      {data &&
        effective?.visualization.showFooter !== false &&
        effective?.renderer !== "billing-matrix" && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              downloadResult(data!, placement.title || widget!.name)
            }
          >
            <Download />
            {tx("CSV")}
          </Button>
        )}
    </section>
  );
}
function DashboardView({
  dashboard,
  state,
  onUpdate,
  onBack,
  onRefresh,
  onEditBinding,
  onDelete,
  onGenerate,
}: {
  dashboard: ReportingDashboard;
  state: ReportingState;
  onUpdate: (d: ReportingDashboard) => void;
  onBack: () => void;
  onRefresh: () => void;
  onEditBinding: (id: string) => void;
  onDelete: () => void;
  onGenerate: (values: Parameters) => void;
}) {
  const [editing, setEditing] = useState(false),
    [placement, setPlacement] = useState<WidgetPlacement>(),
    [lastRefreshed, setLastRefreshed] = useState("");
  const [values, setValues] = useState(defaultsFor(dashboard.parameters));
  const scoped = {
    ...dashboard,
    parameters: dashboard.parameters.map((p) => ({
      ...p,
      default: values[p.name] ?? p.default,
    })),
  };
  const move = (id: string, direction: number) => {
    const widgets = [...dashboard.widgets],
      index = widgets.findIndex((w) => w.id === id),
      next = index + direction;
    if (next < 0 || next >= widgets.length) return;
    [widgets[index], widgets[next]] = [widgets[next], widgets[index]];
    onUpdate({ ...dashboard, widgets });
  };
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button variant="outline" onClick={onBack}>
          <ArrowLeft />
          {tx("Dashboards")}
        </Button>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={onRefresh}>
            <RefreshCw />
            {tx("Refresh")}
          </Button>
          <Button variant="outline" onClick={() => onGenerate(values)}>
            <FilePlus2 />
            {tx("Generate Report")}
          </Button>
          <Button
            variant={editing ? "default" : "outline"}
            onClick={() => setEditing(!editing)}
          >
            <Pencil />
            {tx(editing ? "Done editing" : "Edit dashboard")}
          </Button>
          {editing && (
            <Button
              variant="destructive"
              aria-label={tx("Delete dashboard")}
              onClick={onDelete}
            >
              <Trash2 />
            </Button>
          )}
        </div>
      </div>
      {editing ? (
        <section className={editorPanel}>
          <Field label="Name">
            <Input
              value={dashboard.name}
              onChange={(e) => onUpdate({ ...dashboard, name: e.target.value })}
            />
          </Field>
          <Field label="Description">
            <TextArea
              value={dashboard.description}
              onChange={(e) =>
                onUpdate({ ...dashboard, description: e.target.value })
              }
            />
          </Field>
          <Button
            variant="outline"
            disabled={!state.widgets.length || !state.bindings.length}
            onClick={() =>
              setPlacement({
                id: crypto.randomUUID(),
                widgetId: state.widgets[0].id,
                bindingId: state.bindings[0].id,
                width: "half",
              })
            }
          >
            <Plus />
            {tx("Add Widget")}
          </Button>
        </section>
      ) : (
        <header>
          <h1 className="text-2xl font-semibold">{nameOf(dashboard)}</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {descriptionOf(dashboard)}
          </p>
        </header>
      )}
      <section className={editorPanel}>
        <ParameterFields
          definitions={dashboard.parameters}
          values={values}
          onChange={setValues}
        />
        <p className="text-xs text-muted-foreground">
          {tx("Source as of")} {dashboard.sourceAt} · {tx("Last refreshed")}{" "}
          {lastRefreshed || tx("Loading…")} ·{" "}
          {tx("Auto-refresh every 60 seconds while visible")}
        </p>
      </section>
      <div className="grid grid-cols-12 items-start gap-5">
        {dashboard.widgets.map((p) => (
          <PlacementView
            key={p.id}
            placement={p}
            dashboard={scoped}
            state={state}
            editing={editing}
            onConfigure={() => setPlacement(p)}
            onEditBinding={() => onEditBinding(p.bindingId)}
            onRemove={() =>
              onUpdate({
                ...dashboard,
                widgets: dashboard.widgets.filter((w) => w.id !== p.id),
              })
            }
            onMove={(d) => move(p.id, d)}
            onResolved={() => setLastRefreshed(new Date().toLocaleTimeString())}
          />
        ))}
      </div>
      {!dashboard.widgets.length && (
        <div className={`${editorPanel} text-center`}>
          <p>{tx("Add Widgets to build your Dashboard.")}</p>
          <Button onClick={() => setEditing(true)}>
            {tx("Edit dashboard")}
          </Button>
        </div>
      )}
      <Dialog
        open={!!placement}
        onOpenChange={(open) => {
          if (!open) setPlacement(undefined);
        }}
      >
        <DialogContent className="max-h-[90dvh] overflow-auto sm:max-w-4xl">
          <DialogHeader>
            <DialogTitle>{tx("Configure placement")}</DialogTitle>
            <DialogDescription>
              {tx("Choose a Widget, data binding and placement overrides.")}
            </DialogDescription>
          </DialogHeader>
          {placement && (
            <PlacementEditor
              key={placement.id}
              seed={placement}
              state={state}
              onCancel={() => setPlacement(undefined)}
              onSave={(p) => {
                onUpdate({
                  ...dashboard,
                  widgets: dashboard.widgets.some((w) => w.id === p.id)
                    ? dashboard.widgets.map((w) => (w.id === p.id ? p : w))
                    : [...dashboard.widgets, p],
                });
                setPlacement(undefined);
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
function GenerationDialog({
  template,
  state,
  initialValues,
  onClose,
  onSaved,
}: {
  template: ReportTemplateDefinition;
  state: ReportingState;
  initialValues: Parameters;
  onClose: () => void;
  onSaved: (report: GeneratedReport) => void;
}) {
  const [values, setValues] = useState({
      ...defaultsFor(template.parameters),
      ...initialValues,
    }),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const controller = useRef<AbortController | null>(null),
    engine = useEngine();
  useEffect(() => () => controller.current?.abort(), []);
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) {
          controller.current?.abort();
          onClose();
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{tx("Generate Report")}</DialogTitle>
          <DialogDescription>{nameOf(template)}</DialogDescription>
        </DialogHeader>
        <ParameterFields
          definitions={template.parameters}
          values={values}
          onChange={setValues}
        />
        <p className="text-xs text-muted-foreground">
          {tx(
            "The document captures these parameters, language and data. Later edits do not change it.",
          )}
        </p>
        {error && (
          <p role="alert" className="text-sm text-red-700">
            {error}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            onClick={() => {
              controller.current?.abort();
              onClose();
            }}
          >
            {tx("Cancel")}
          </Button>
          <Button
            disabled={busy}
            onClick={async () => {
              setError("");
              setBusy(true);
              const run = new AbortController();
              controller.current = run;
              try {
                const report = await generateReport(
                  state,
                  template.id,
                  values,
                  getLocale(),
                  engine.query,
                  run.signal,
                );
                run.signal.throwIfAborted();
                await reportRepository.save(report);
                onSaved(report);
              } catch (e) {
                if (!run.signal.aborted) setError((e as Error).message);
              } finally {
                setBusy(false);
              }
            }}
          >
            {tx(busy ? "Generating…" : "Generate and save")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function BindingSummary({
  binding,
  state,
  onOpen,
}: {
  binding: DataBindingDefinition;
  state: ReportingState;
  onOpen: (page: PageId, id?: string) => void;
}) {
  const dashboards = state.dashboards.filter((dashboard) =>
    dashboard.widgets.some((placement) => placement.bindingId === binding.id),
  );
  const templates = state.templates.filter(
    (template) =>
      template.bindingIds.includes(binding.id) ||
      template.sections.some(
        (section) =>
          (section.kind === "widget" && section.bindingId === binding.id) ||
          section.condition?.bindingId === binding.id,
      ),
  );
  const widgetIds = new Set([
    ...dashboards.flatMap((dashboard) =>
      dashboard.widgets
        .filter((placement) => placement.bindingId === binding.id)
        .map((placement) => placement.widgetId),
    ),
    ...templates.flatMap((template) =>
      template.sections.flatMap((section) =>
        section.kind === "widget" && section.bindingId === binding.id
          ? [section.widgetId]
          : [],
      ),
    ),
  ]);
  const groups: {
    label: string;
    page: PageId;
    items: { id: string; name: string; builtin?: boolean }[];
    empty: string;
  }[] = [
    {
      label: "Powers widgets",
      page: "widgets",
      items: state.widgets.filter((widget) => widgetIds.has(widget.id)),
      empty: "No widgets connected yet",
    },
    {
      label: "Dashboards",
      page: "dashboards",
      items: dashboards,
      empty: "Not used in a dashboard yet",
    },
    {
      label: "Report Templates",
      page: "templates",
      items: templates,
      empty: "Not used in a report template yet",
    },
  ];
  return (
    <dl className="flex-1 space-y-4 text-sm">
      {groups.map((group) => (
        <ConfigurationDetail
          key={group.page}
          label={tx(group.label)}
          value={
            group.items.length ? (
              <ul className="space-y-1">
                {group.items.map((item) => (
                  <li key={item.id}>
                    <button
                      type="button"
                      className="max-w-full break-words text-left underline-offset-4 hover:underline focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      onClick={() => onOpen(group.page, item.id)}
                    >
                      {nameOf(item)}
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <span className="text-muted-foreground">{tx(group.empty)}</span>
            )
          }
        />
      ))}
    </dl>
  );
}

function ConfigurationDetail({
  label,
  value,
}: {
  label: string;
  value: ReactNode;
}) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd
        className="mt-0.5 break-words"
        title={typeof value === "string" ? value : undefined}
      >
        {value}
      </dd>
    </div>
  );
}
function WidgetConfiguration({
  widget,
  state,
  onOpenBinding,
}: {
  widget: WidgetDefinition;
  state: ReportingState;
  onOpenBinding: (id: string) => void;
}) {
  const bindingIds = new Set([
    ...state.dashboards.flatMap((d) =>
      d.widgets.filter((p) => p.widgetId === widget.id).map((p) => p.bindingId),
    ),
    ...state.templates.flatMap((t) =>
      t.sections.flatMap((s) =>
        s.kind === "widget" && s.widgetId === widget.id ? [s.bindingId] : [],
      ),
    ),
  ]);
  const bindings = state.bindings.filter((b) => bindingIds.has(b.id));
  const v = widget.visualization;
  const presentations = {
    kpi: "KPI",
    table: "Table",
    bar: "Bar",
    line: "Line",
    pie: "Pie",
  };
  return (
    <dl className="min-h-0 space-y-2 text-xs [&_dd]:line-clamp-1 [&_dd]:break-all">
      <ConfigurationDetail
        label={tx("Data bindings")}
        value={
          bindings.length ? (
            <div className="flex min-w-0 gap-3">
              {bindings.map((b) => (
                <button
                  key={b.id}
                  className="min-w-0 truncate text-left text-primary underline underline-offset-2 hover:text-primary/80"
                  title={nameOf(b)}
                  onClick={() => onOpenBinding(b.id)}
                >
                  {nameOf(b)}
                </button>
              ))}
            </div>
          ) : (
            tx("Not connected")
          )
        }
      />
      <div className="grid grid-cols-2 gap-3">
        <ConfigurationDetail
          label={tx("Visualization")}
          value={
            widget.renderer === "billing-matrix"
              ? tx("Billing matrix")
              : tx(presentations[v.type])
          }
        />
        <ConfigurationDetail label={tx("Value format")} value={tx(v.format)} />
      </div>
      <ConfigurationDetail
        label={tx("Display fields")}
        value={
          (v.columns?.length ? v.columns : [v.x, ...v.y].filter(Boolean))
            .map((field) => tx(v.fieldLabels?.[field] ?? field))
            .join(", ") || tx("All result columns")
        }
      />
      {v.target !== undefined && (
        <ConfigurationDetail label={tx("Target")} value={v.target} />
      )}
    </dl>
  );
}
